import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DateCommonService } from '@nttp/common-service/date-common';
import { UtilityService } from '@nttp/common-service/utility-service';
import { FundAccountModalComponent } from '@pg-app/client/client-dashboard/components/fund-account/modal/modal.component';
import { FundAccountModel } from '@pg-app/client/client-dashboard/components/fund-account/service/fund-account.model';
import { FundAccountService } from '@pg-app/client/client-dashboard/components/fund-account/service/fund-account.service';
import { IPricingData, PricingService } from '@pg-app/core/services/signalR/pricing.service';
import { format } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { RegistrationStep2Service } from '@pg-app/client/services/registrationStep2.service';
import { StoreProvider } from '@pg-app/core/services/storeProvider/store.provider';
import { ShareService } from '@nttp/common-service/share-service';
import { environment } from '@pg-env/environment';
import { UserRoleEnum } from '@nttp/enum/user-role.enum';
import { CommonMessages } from '@pg-app/shared/constants/common-messages';
import { TradeTypeEnum } from '@nttp/common-service/trade-type-enum';
import { CurrencyService } from '@pg-app/partner/dashboard/services/currency.service';

@Component({
  selector: 'np-fund-account',
  templateUrl: './fund-account.component.html',
  styleUrls: ['./fund-account.component.scss']
})
export class FundAccountComponent implements OnInit, OnDestroy {
  public fundAccountForm: FormGroup;
  public currencys: Array<any> = [];
  public allowedTradeTypes: Array<any> = [];
  public isGettedRate = false;
  public clientRate: Number;
  isSellChange = true;
  public minDate: Date;
  public maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1));
  public myFilter: any;
  brandName = environment.brandName;

  public exchangeFee: Number;
  public holidays = [];
  quoteId: string = null;

  sellCurrencies: Array<any> = [];
  buyCurrencies: Array<any> = [];
  DIRECTION = {
    SELL: 1,
    BUY: 2,
    SELL_AND_BUY: 3
  };

  RATECHANGE = {
    INCREASE: 1,
    DECREASE: 2
  };
  rateChange = null;
  isForwardTrade = false;

  public deposited: number;
  depositTickBox: FormGroup;
  showDepositCurrency = false;
  depositRequired = false;

  overCutOffTime: boolean;
  tradeType = null;

  userRoleEnum = UserRoleEnum;
  pastCutoffTime: boolean;

  get getAmountBuy(): FormControl {
    return this.fundAccountForm.get('buyAmount') as FormControl;
  }

  get getAmountPay(): FormControl {
    return this.fundAccountForm.get('payAmount') as FormControl;
  }

  get getCurrencyPay(): FormControl {
    return this.fundAccountForm.get('currencyPay') as FormControl;
  }

  get getCurrencyBuy(): FormControl {
    return this.fundAccountForm.get('currencyBuy') as FormControl;
  }

  get getValueDate(): FormControl {
    return this.fundAccountForm.get('valueDate') as FormControl;
  }

  get ForwardRatesNotWork(): string {
    return 'ForwardRatesNotWork';
  }

  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    private utilService: UtilityService,
    private toasterService: ToastrService,
    private fundAccountService: FundAccountService,
    private fundAccountModel: FundAccountModel,
    private pricingService: PricingService,
    private dateService: DateCommonService,
    public registrationService: RegistrationStep2Service,
    private store: StoreProvider,
    private shareService: ShareService,
    private tradeTypeEnum: TradeTypeEnum,
    private currencyService: CurrencyService
  ) {}

  ngOnInit() {
    this.fundAccountForm = this.fb.group({
      buyAmount: [''],
      payAmount: [''],
      currencyBuy: ['', Validators.required],
      currencyPay: ['', Validators.required],
      valueDate: ['', Validators.required]
    });

    this.depositTickBox = this.fb.group({
      depositCurrency: ['']
    });

    this.getCurrencies();
    this.checkSameDayCutOff();
    this.myFilter = (d: Date): boolean => {
      const day = d.getDay();
      return day !== 0 && day !== 6;
    };
    this.pricingService.offListeners();
  }

  checkSameDayCutOff() {
    this.fundAccountService.checkSameDayCutOff().subscribe(res => {
      this.pastCutoffTime = res;
    });
  }

  ngOnDestroy() {
    this.pricingService.offListeners();
    this.pricingService.stopConnection();
  }

  changeCurrency() {
    this.changeValueItem();
    if (this.getCurrencyPay.value && this.getCurrencyBuy.value) {
      this.getHolidays();
      this.getTradeTypesByCurrencyPair();
    }
  }

  public getCurrencies() {
    this.shareService.currencyList$.subscribe(data => {
      this.currencys = data || [];

      this.sellCurrencies = this.currencys.filter(
        p => p.direction === this.DIRECTION.SELL || p.direction === this.DIRECTION.SELL_AND_BUY
      );

      this.buyCurrencies = this.currencys.filter(
        p => p.direction === this.DIRECTION.BUY || p.direction === this.DIRECTION.SELL_AND_BUY
      );
    });
  }

  public changeAmount(type) {
    if (this.getAmountBuy.value && this.getAmountPay.value) {
      if (type === 'buy') {
        this.getAmountPay.setValue(null);
      } else {
        this.getAmountBuy.setValue(null);
      }
    }
    this.changeValueItem();
  }

  public getRate() {
    this.fundAccountForm.markAllAsTouched();

    if (this.isGettedRate) {
      return;
    }

    if (!this.fundAccountForm.get('currencyBuy').value) {
      this.toasterService.error('Please select buy currency');
      return;
    }
    if (!this.fundAccountForm.get('currencyPay').value) {
      this.toasterService.error('Please select pay with currency');
      return;
    }
    if (this.fundAccountForm.get('currencyBuy').value === this.fundAccountForm.get('currencyPay').value) {
      this.toasterService.error('The buy currency should be different from the pay with currency');
      return;
    }

    const amount = this.getAmountBuy.value ? this.getAmountBuy.value : this.getAmountPay.value;
    if (parseFloat(amount) === 0) {
      this.toasterService.error('Amount must be greater than 0!');
      return;
    }

    if (!amount) {
      this.toasterService.error('Please enter an amount in the Buy or Pay with fields');
      return;
    }

    if (!this.fundAccountForm.get('valueDate').value) {
      this.toasterService.error('Please select date');
      return;
    }

    if (this.dateService.isHoliday(this.fundAccountForm.get('valueDate').value, this.holidays)) {
      this.toasterService.error('Non-working day can not be selected');
      return;
    }

    if (new Date(this.fundAccountForm.get('valueDate').value) > this.maxDate) {
      this.toasterService.error(`Please contact your relationship manager for this trade on ${environment.phone}.`);
      return;
    }

    const paramCurrencyPairAvailableOnPortal = {
      buyCurrencyId: this.getCurrencyBuy.value.id,
      sellCurrencyId: this.getCurrencyPay.value.id
    };
    
    // Check if currency pair is available on portal
    this.currencyService.currencyPairIsAvailableOnPortal(paramCurrencyPairAvailableOnPortal).subscribe(
      res => {
        if (res.error) {
          this.toasterService.error(res.error);
        } 
        else {
          this.isSellChange = !!this.getAmountPay.value;

          this.getClientRate(
            this.getCurrencyPay.value.currencyTypeId,
            this.getCurrencyPay.value.code,
            this.getCurrencyBuy.value.currencyTypeId,
            this.getCurrencyBuy.value.code,
            amount
          );
      
          if (this.checkDeposit()) {
            this.getDeposited();
          } else {
            this.showDepositCurrency = false;
          }
        }
      },
      () => {
        this.toasterService.error(CommonMessages.errorMessageAPI);
      }
    );
  }

  public buyNow() {
    this.depositRequired = false;

    if (this.fundAccountForm.valid && this.isGettedRate) {

      this.utilService.isAccountOperational().subscribe(isOk => {
        if (!isOk) {
          this.toasterService.error(CommonMessages.accountNotOperational);
        } else if (this.checkDeposit() && Number(this.deposited) > 0) {
          this.depositTickBox.get('depositCurrency').setValue(null);
          this.showDepositCurrency = true;
          this.depositRequired = true;
        } else if (this.getValueDate.value.setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0)) {
          this.fundAccountService.checkSameDayCutOff().subscribe(res => {
            this.overCutOffTime = res;
            if (!res) {
              this.forwardTradeCheckWithTradeTypeThenBookTrade();
            }
          });
        } else {
          this.forwardTradeCheckWithTradeTypeThenBookTrade();
        }
      });

    }
  }

  private forwardTradeCheckWithTradeTypeThenBookTrade() {
    const param = {
      incomingSellCurrencyId : this.getCurrencyPay.value.id,
      incomingBuyCurrencyId: this.getCurrencyBuy.value.id,

      incomingSellQuantity: parseFloat(this.getAmountPay.value),
      incomingBuyQuantity: parseFloat(this.getAmountBuy.value),

      valueDateString: format(this.getValueDate.value, 'YYYYMMDD')
    };

    this.fundAccountService.forwardTradesCheckWithTradeType(param).subscribe(
      res => {
        if (res.isWithinTheThreshold === true) {
          this.bookTrade();
        } else if (res.validationMessage) {
          this.toasterService.error(res.validationMessage);
        } else {
          this.toasterService.error(CommonMessages.errorMessageAPI);
        }
      }
    )
  }

  private bookTrade() {
    this.fundAccountModel = {
      buyAmount: parseFloat(this.getAmountBuy.value),
      payAmount: parseFloat(this.getAmountPay.value),
      currencyCodeBuy: this.getCurrencyBuy.value,
      currencyCodePay: this.getCurrencyPay.value,
      exchangeRate: Number(this.clientRate),
      exchangeFee: Number(this.exchangeFee),
      valueDate: this.getValueDate.value,
      tradeReference: '',
      tradeId: '',
      deposited: this.deposited,
      showDeposited: this.checkDeposit(),
      isSellChanged: this.isSellChange,
      isForwardTrade: this.isForwardTrade,
      holidays: this.holidays,
      depositCurrencyId: this.depositRequired ? this.depositTickBox.get('depositCurrency').value : null,
      iotInfo: { redirectedFromIOT: false, processedOkIOT: false },
      quoteId: this.quoteId
    };

    this.showDialog();
  }

  confirmDeposit() {
    if (this.depositTickBox.get('depositCurrency').value) {
      this.forwardTradeCheckWithTradeTypeThenBookTrade();
      this.showDepositCurrency = false;
    }
  }

  cancelDeposit() {
    this.showDepositCurrency = false;
  }

  public showDialog() {
    this.pricingService.offListeners();
    this.pricingService.stopConnection();
    const dialogRef = this.dialog.open(FundAccountModalComponent, {
      panelClass: 'custom-dialog',
      disableClose: true,
      data: this.fundAccountModel
    });
    dialogRef.afterClosed().subscribe(() => {
      this.registerPricingService(
        this.tradeType.id,
        this.isForwardTrade,
        this.getCurrencyPay.value.currencyTypeId,
        this.getCurrencyPay.value.code,
        this.getCurrencyBuy.value.currencyTypeId,
        this.getCurrencyBuy.value.code,
        0,
        this.exchangeFee,
        this.quoteId
      );
    });
  }

  public switchValue() {
    if (this.getAmountPay.value || this.getAmountBuy.value) {
      const payAmount = this.getAmountPay.value;
      const buyAmount = this.getAmountBuy.value;
      this.getAmountPay.setValue(buyAmount);
      this.getAmountBuy.setValue(payAmount);
      if (this.getAmountPay.value && this.getAmountBuy.value) {
        this.changeValueItem();
      }
    }
  }

  public checkDeposit() {
    const fundAccountForm = new Date(this.getValueDate.value);
    fundAccountForm.setHours(0, 0, 0, 0);

    const date = this.dateService.getWorkingDayWithWeekendAndHoliday(new Date(), 6, this.holidays);
    date.setHours(0, 0, 0, 0);

    return fundAccountForm >= date;
  }

  public checkDateChange() {
    this.changeValueItem();
  }

  public dateFilter() {
    this.myFilter = (d: Date): boolean => {
      return this.dateService.dateFilter(d, this.holidays);
    };
  }

  public getHolidays(callback = null) {
    if (!this.getCurrencyPay.value && !this.getCurrencyBuy.value) {
      return;
    }
    const fromDate = format(this.minDate, 'YYYYMMDD');
    const toDate = format(this.maxDate, 'YYYYMMDD');
    const currencyCodes = [];

    if (this.getCurrencyBuy.value) {
      currencyCodes.push(this.getCurrencyBuy.value.code);
    }

    if (this.getCurrencyPay.value) {
      currencyCodes.push(this.getCurrencyPay.value.code);
    }

    this.utilService.getHolidays(fromDate, toDate, currencyCodes).subscribe(holidays => {
      this.holidays = holidays || [];
      this.dateFilter();

      if (callback && typeof callback === 'function') {
        callback(holidays);
      }
    });
  }

  public registerPricingService(
    tradeTypeId: string,
    isForwardTrade: boolean,
    currencyTypeSellId: string,
    sellCode: string,
    currencyTypeBuyId: string,
    buyCode: string,
    amount: Number,
    exchangeFee: Number,
    quoteId: string
  ) {
    if (
      !tradeTypeId ||
      !this.fundAccountForm.get('currencyPay').value ||
      !this.fundAccountForm.get('currencyBuy').value ||
      (!this.getAmountBuy.value && !this.getAmountPay.value)
    ) {
      return;
    }

    this.onListenEventPricing();

    this.pricingService.registerPricingService(
      tradeTypeId,
      isForwardTrade,
      currencyTypeSellId,
      sellCode,
      this.fundAccountForm.get('payAmount').value ? parseFloat(this.fundAccountForm.get('payAmount').value) : 0,
      currencyTypeBuyId,
      buyCode,
      this.isSellChange,
      this.fundAccountForm.get('buyAmount').value ? parseFloat(this.fundAccountForm.get('buyAmount').value) : 0,
      format(this.getValueDate.value, 'YYYYMMDD'),
      exchangeFee,
      quoteId
    );
  }

  public onListenEventPricing() {
    this.pricingService.pricingEvent.subscribe(data => {
      if (data && data.status === 'Ok') {
        if (!environment.production) {
          console.log('Received Rate ' + data.clientRate);
        }
        this.rateChange =
          data.clientRate > this.clientRate
            ? this.RATECHANGE.INCREASE
            : data.clientRate < this.clientRate
              ? this.RATECHANGE.DECREASE
              : this.rateChange;
        this.clientRate = data.clientRate;
        this.exchangeFee = data.exchangeFee;
        this.quoteId = data.quoteId;
        this.changeValueAmount(data);
        return;
      }
    });
  }

  public changeValueAmount(data: IPricingData = null) {
    if (data.type === 'UpdateSell') {
      this.getAmountPay.setValue(data.sellAmount);
    } else if (data.type === 'UpdateBuy') {
      this.getAmountBuy.setValue(data.buyAmount);
    }
    this.quoteId = data.quoteId;
  }

  public getClientRate(
    currencyTypeSellId: string,
    sellCode: string,
    currencyTypeBuyId: string,
    buyCode: string,
    amount: Number,
    bookTrade = false
  ) {
    const currencyCodes = [this.getCurrencyBuy.value.code, this.getCurrencyPay.value.code];
    let tradeTypeId = '';
    this.quoteId = null;

    this.utilService
      .getTradeType(format(this.getValueDate.value, 'YYYY-MM-DD'), format(new Date(), 'YYYY-MM-DD'), currencyCodes)
      .subscribe(data => {
        if (data && data.length > 0) {
          if (data.length === 1) {
            this.tradeType = data[0];
            tradeTypeId = data[0].id;
            this.isForwardTrade = false;
          } else {
            const tradeName = 'Fixed date forward (FDF)';
            const trade = data.find(item => item.tradeTypeName.toLowerCase() === tradeName.toLowerCase());
            this.isForwardTrade = true;
            if (trade) {
              tradeTypeId = trade.id;
              this.tradeType = trade;
            }
          }

          if (this.allowedTradeTypes &&
              this.allowedTradeTypes.length > 0 &&
              this.allowedTradeTypes.find(p => p.tradeTypeName === this.tradeType.tradeTypeName)
              )  {
                console.trace ('TradeType permitted for Currency Pair')

                this.pricingService
                .getExchangeFee(
                  tradeTypeId,
                  currencyTypeSellId,
                  currencyTypeBuyId,
                  sellCode,
                  buyCode,
                  this.isSellChange,
                  amount,
                  '',
                  this.isForwardTrade,
                  format(this.getValueDate.value, 'YYYYMMDD')
                )
                .subscribe(exchangeFeeDto => {
                  this.exchangeFee = exchangeFeeDto.exchangeFee;
                  this.quoteId = exchangeFeeDto.quoteId;

                  if (!this.isForwardTrade) {
                    // Spot rate
                    this.pricingService
                      .getClientRate(
                        sellCode,
                        buyCode,
                        this.exchangeFee,
                        format(this.getValueDate.value, 'YYYYMMDD'),
                        this.quoteId,
                        ''
                      )
                      .subscribe(clientRateDto => {
                        this.clientRate = parseFloat(clientRateDto.clientRate);
                        this.calculatePayWithAndAmountConverted(isLoadClientRate => {
                          if (isLoadClientRate) {
                            this.isGettedRate = true;
                            if (bookTrade) {
                              this.bookTrade();
                            }
                            this.registerPricingService(
                              tradeTypeId,
                              this.isForwardTrade,
                              currencyTypeSellId,
                              sellCode,
                              currencyTypeBuyId,
                              buyCode,
                              amount,
                              this.exchangeFee,
                              this.quoteId
                            );
                          }
                        });
                      });
                  } else {
                    // Forward Rate
                    this.pricingService
                      .getClientForwardRate(
                        sellCode,
                        buyCode,
                        this.exchangeFee,
                        format(this.getValueDate.value, 'YYYYMMDD'),
                        this.quoteId,
                        ''
                      )
                      .subscribe(
                        clientRateDto => {
                          this.clientRate = parseFloat(clientRateDto.clientRate);

                          if (this.clientRate == -1) {
                            this.toasterService.error(`Rate for pair of ${sellCode} and ${buyCode} is not available at the moment.`);
                          }
                          else {
                            this.calculatePayWithAndAmountConverted(isLoadClientRate => {
                              if (isLoadClientRate) {
                                this.isGettedRate = true;
                                this.registerPricingService(
                                  tradeTypeId,
                                  this.isForwardTrade,
                                  currencyTypeSellId,
                                  sellCode,
                                  currencyTypeBuyId,
                                  buyCode,
                                  amount,
                                  this.exchangeFee,
                                  this.quoteId
                                );
                              }
                            });
                          }
                        },
                        () => {
                          this.toasterService.error(CommonMessages.tradeUnsupportedError);
                        }
                      );
                  }
                });

              }
              else{
                console.trace ('Currency Pair does not support Trade Type');
                this.toasterService.error(CommonMessages.tradeUnsupportedError);
              }
        }
      });
  }


  calculatePayWithAndAmountConverted(callback = null) {
    const buyAmount: Number = this.getAmountBuy.value ? parseFloat(this.getAmountBuy.value) : 0;
    const sellAmount: Number = this.getAmountPay.value ? parseFloat(this.getAmountPay.value) : 0;
    const clientRate: Number = this.clientRate;
    const sellCurrencyCode = this.getCurrencyPay.value.code;
    const buyCurrencyCode = this.getCurrencyBuy.value.code;

    this.updateByClientRate(
      buyCurrencyCode,
      buyAmount,
      sellCurrencyCode,
      sellAmount,
      clientRate,
      this.isSellChange,
      this.quoteId,
      (pricingData: IPricingData) => {
        this.getAmountPay.setValue(pricingData.sellAmount);
        this.getAmountBuy.setValue(pricingData.buyAmount);

        const newTrade = {
          valueDate: format(this.fundAccountForm.get('valueDate').value, 'YYYY/MM/DD'),
          exchangeRate: pricingData.clientRate,
          sellCurrencyId: this.fundAccountForm.get('currencyPay').value.id,
          sellAmount: pricingData.sellAmount,
          buyCurrencyId: this.fundAccountForm.get('currencyBuy').value.id,
          buyAmount: pricingData.buyAmount,
          userId: this.store.get('userId')
        };

        sessionStorage.setItem('newTrade', JSON.stringify(newTrade));
        sessionStorage.setItem('clickGetRate', 'true');
        sessionStorage.setItem('clickBuyNow', 'false');

        if (callback && typeof callback === 'function') {
          callback(true);
        }
      }
    );
  }

  updateByClientRate(
    buyCurrencyCode: string,
    buyAmount: Number,
    sellCurrencyCode: string,
    sellAmount: Number,
    clientRate: Number,
    isSellChanged: boolean,
    quoteId: string,
    callback = null
  ) {
    this.pricingService
      .updateByClientRate(buyCurrencyCode, buyAmount, sellCurrencyCode, sellAmount, clientRate, isSellChanged, quoteId)
      .subscribe(
        (pricingData: IPricingData) => {
          if (!pricingData) {
            return;
          }
          if (callback && typeof callback === 'function') {
            callback(pricingData);
          }
        },
      );
  }

  getDeposited() {
    this.fundAccountService.getDeposited().subscribe(
      _result => {
        this.deposited = Number(_result);
      },
    );
  }

  changeValueItem() {
    if (this.isGettedRate) {
      this.isGettedRate = false;
      this.pricingService.offListeners();
      this.pricingService.stopConnection();
      this.clientRate = null;
      this.exchangeFee = null;
      this.rateChange = null;
      if (this.getAmountBuy.value && this.getAmountPay.value) {
        if (!this.isSellChange) {
          this.getAmountPay.setValue(null);
        } else {
          this.getAmountBuy.setValue(null);
        }
      }
    }
  }

  togglePicker(picker) {
    if (!this.getCurrencyBuy.value || !this.getCurrencyPay.value) {
      return;
    }
    picker.opened ? picker.close() : picker.open();
  }

  // Same day tradeing cut off action
  continue() {
    this.overCutOffTime = false;
    const newValueDate = this.dateService.getWorkingDayWithWeekendAndHoliday(this.getValueDate.value, 1, this.holidays);
    this.getValueDate.setValue(new Date(newValueDate));

    const amount = this.isSellChange ? parseFloat(this.getAmountPay.value) : parseFloat(this.getAmountBuy.value);
    this.rateChange = null;

    this.getClientRate(
      this.getCurrencyPay.value.currencyTypeId,
      this.getCurrencyPay.value.code,
      this.getCurrencyBuy.value.currencyTypeId,
      this.getCurrencyBuy.value.code,
      amount,
      true
    );
  }

  cancel() {
    this.overCutOffTime = false;
  }

  getTradeTypesByCurrencyPair() {
    const params = {
      buyCurrencyId: this.getCurrencyBuy.value.id,
      sellCurrencyId: this.getCurrencyPay.value.id
    };
    this.fundAccountService.getTradeTypeByCurrencyPair(params).subscribe(res => {
      const allowSameDay = !!res.find(p => p.tradeTypeName === this.tradeTypeEnum.SAME_DAY);
      this.allowedTradeTypes = res;
      this.minDate = allowSameDay && !this.pastCutoffTime ? this.dateService.getWorkingDayWithWeekendAndHoliday(new Date(), 0, this.holidays)
      : this.dateService.getWorkingDayWithWeekendAndHoliday(new Date(), 1, this.holidays);
    });
  }
}
